import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext"; // Para obtener el token JWT
import axios from "axios"; // Para hacer peticiones HTTP
import "./ManageTemplatesPage.css"; // Crearemos este archivo CSS
import { Link } from "react-router-dom"; // Importar Link

function ManageTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState(""); // Para mensajes de éxito/error de acciones
  const { token } = useAuth(); // Obtiene el token del contexto

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Función para obtener todas las plantillas del backend
  const fetchTemplates = async () => {
    setIsLoading(true);
    setError("");
    setMessage("");
    try {
      const response = await axios.get(`${API_BASE_URL}/templates`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setTemplates(response.data);
    } catch (err) {
      setError(err.response?.data?.error || "Error al cargar las plantillas.");
      console.error("Error fetching templates:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Efecto para cargar las plantillas al montar el componente o cuando el token cambia
  useEffect(() => {
    if (token) {
      fetchTemplates();
    } else {
      setError("No autenticado. Por favor, inicie sesión.");
      setIsLoading(false);
    }
  }, [token, API_BASE_URL]); // Dependencias: token y URL base

  // Función para activar una plantilla
  const handleSetActive = async (templateId) => {
    setMessage("");
    setError("");
    try {
      const response = await axios.post(
        `${API_BASE_URL}/templates/${templateId}/set-active`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setMessage(response.data.message);
      // Refrescar la lista de plantillas para ver el cambio de estado
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || "Error al activar la plantilla.");
      console.error("Error activating template:", err);
    }
  };

  // Función para editar una plantilla (por ahora, solo un placeholder)
  const handleEditTemplate = (templateId) => {
    // Implementaremos la navegación a una página de edición más adelante
    console.log(`Editar plantilla con ID: ${templateId}`);
    setMessage(
      `Funcionalidad de edición para plantilla ${templateId} pendiente.`
    );
  };

  // Función para eliminar una plantilla (por ahora, solo un placeholder)
  const handleDeleteTemplate = async (templateId) => {
    if (
      !window.confirm(
        "¿Está seguro de que desea eliminar esta plantilla? Esta acción no se puede deshacer."
      )
    ) {
      return;
    }
    setMessage("");
    setError("");
    try {
      await axios.delete(`${API_BASE_URL}/templates/${templateId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMessage("Plantilla eliminada exitosamente.");
      fetchTemplates(); // Refrescar la lista
    } catch (err) {
      setError(err.response?.data?.error || "Error al eliminar la plantilla.");
      console.error("Error deleting template:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="manage-templates-container">
        <p>Cargando plantillas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-templates-container">
        <p className="error-message">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="manage-templates-container">
      <div className="manage-templates-card">
        <h2 className="manage-templates-title">
          Gestión de Plantillas de Certificados
        </h2>
        <p className="manage-templates-description">
          Administra las plantillas disponibles para la generación de
          certificados.
        </p>

        {message && (
          <p className="manage-templates-message success">{message}</p>
        )}
        {error && <p className="manage-templates-message error">{error}</p>}

        {templates.length === 0 ? (
          <p>
            No hay plantillas registradas.{" "}
            <Link to="/admin/templates/create">Crea una ahora</Link>.
          </p>
        ) : (
          <table className="templates-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Activa</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id}>
                  <td>{template.name}</td>
                  <td>{template.description || "Sin descripción"}</td>
                  <td>
                    {template.is_active ? (
                      <span className="active-badge">Activa</span>
                    ) : (
                      <button
                        onClick={() => handleSetActive(template.id)}
                        className="activate-button"
                        disabled={isLoading}
                      >
                        Activar
                      </button>
                    )}
                  </td>
                  <td>
                    {/* Contenedor para los botones de acción */}
                    <div className="action-buttons-container">
                      <button
                        onClick={() => handleEditTemplate(template.id)}
                        className="action-button edit-button"
                        disabled={isLoading}
                      >
                        <span className="material-icons">edit</span>
                        <span className="button-text">Editar</span>
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="action-button delete-button"
                        disabled={isLoading || template.is_active} // No eliminar si está activa
                      >
                        <span className="material-icons">delete</span>
                        <span className="button-text">Eliminar</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ManageTemplatesPage;
